import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import api from "../../../../utils/ApiMethod";
import PageTitle from "../../../../components/PageTitle";
import {
  Button,
  Card,
  Col,
  Form,
  FormControl,
  FormGroup,
  FormLabel,
  Pagination,
  Row,
} from "react-bootstrap";
import Table from "../../../../components/Table";
import QueryViewModal from "./QueryViewModal";
import QueryUpdateModal from "./QueryUpdateModal";
import StatusBadge from "./StatusBadge";
import DistrictQuerySummaryModal, {
  DistrictQuerySummaryRow,
} from "./DistrictQuerySummaryModal";
import Spinner from "../../../../components/Spinner";
import StatisticsWidget from "../../../widgets/StatisticsWidget";
import { RootState } from "../../../../redux/store";
import { logoutUser } from "../../../../redux/actions";
import { ADMIN_LOGIN_BASE_URL } from "../../../../apiservices/apiService";
import styles from "./AllQuery.module.css";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SEARCH_DEBOUNCE_MS = 400;

const AllQuery = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<any[]>([]);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedStatusOfQuery, setSelectedStatusOfQuery] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  // Level/status only take effect once "Filter" is clicked, decoupled from
  // the raw dropdown selections so picking an option doesn't fetch early.
  const [appliedLevel, setAppliedLevel] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const [appliedDistrict, setAppliedDistrict] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [overallTotal, setOverallTotal] = useState(0);
  const [statusSummary, setStatusSummary] = useState<Record<string, number>>({
    Open: 0,
    "In-Review": 0,
    Pending: 0,
    Closed: 0,
  });
  const [districtSummary, setDistrictSummary] = useState<
    DistrictQuerySummaryRow[]
  >([]);
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);

  const { user } = useSelector((state: RootState) => ({
    user: state.Auth.user,
  }));
  const profileId = user?._id ? user._id : user?.user ? user.user._id : "";
  const Key_Key = user?.moniterd_ttl
    ? user.moniterd_ttl
    : user?.user
    ? user.user.moniterd_ttl
    : "";

  // Debounce the search box so every keystroke doesn't fire a request,
  // and jump back to page 1 whenever the search term actually changes.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleSessionExpired = () => {
    alert("Session Expired");
    dispatch(logoutUser());
    window.location.href = ADMIN_LOGIN_BASE_URL;
  };

  const fetchQueries = useCallback(async () => {
    if (!profileId || !Key_Key) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search) params.set("search", search);
      if (appliedLevel) params.set("level", appliedLevel);
      if (appliedStatus) params.set("status", appliedStatus);
      if (appliedDistrict) params.set("district", appliedDistrict);

      const response = await api.fetchDataAuth(
        `/api/query/${profileId}/${Key_Key}?${params.toString()}`
      );

      if (response?.status === 440) {
        handleSessionExpired();
        return;
      }
      if (response?.status && response.status >= 400) {
        setError(response?.message || "Failed to fetch queries.");
        setData([]);
        return;
      }

      const queries = response?.data?.data || [];
      setData(queries);
      setTotal(response?.data?.total ?? 0);
      setTotalPages(response?.data?.totalPages ?? 1);
      setOverallTotal(response?.data?.overallTotal ?? 0);
      if (response?.data?.statusSummary) {
        setStatusSummary(response.data.statusSummary);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to fetch details. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    search,
    appliedLevel,
    appliedStatus,
    appliedDistrict,
    profileId,
    Key_Key,
  ]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  // All 38 districts (A-Z, with open counts) for the district dropdown - one
  // small Redis-cached response, fetched once here and again only after a
  // status update. The summary modal pages through the same endpoint itself.
  const fetchDistrictOptions = useCallback(async () => {
    if (!profileId || !Key_Key) return;
    try {
      const response = await api.fetchDataAuth(
        `/api/query/district-summary/${profileId}/${Key_Key}?limit=50&sort=district`
      );
      if (response?.status === 440) {
        handleSessionExpired();
        return;
      }
      if (response?.data?.status && Array.isArray(response.data.data)) {
        setDistrictSummary(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching district list:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId, Key_Key]);

  useEffect(() => {
    fetchDistrictOptions();
  }, [fetchDistrictOptions]);

  const handleQueryUpdated = useCallback(() => {
    fetchQueries();
    fetchDistrictOptions();
    setSummaryRefreshKey((k) => k + 1);
  }, [fetchQueries, fetchDistrictOptions]);

  const appliedDistrictName =
    districtSummary.find((d) => d.code === appliedDistrict)?.district ||
    appliedDistrict;

  const columns = useMemo(
    () => [
      {
        Header: "S.NO",
        accessor: "index",
        Cell: ({ row }: { row: any }) => (
          <span className="text-muted">
            {(page - 1) * limit + row.index + 1}
          </span>
        ),
        sort: false,
      },
      {
        Header: "Name",
        accessor: "name",
        sort: false,
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ value }: { value: string }) => (
          <span className={styles.emailCell}>{value}</span>
        ),
        sort: false,
      },
      {
        Header: "Phone Number",
        accessor: "phone_number",
        sort: false,
      },
      {
        Header: "Query Coming From Level",
        accessor: "queryOfLevel",
        sort: false,
      },
      {
        Header: "District",
        accessor: "district",
        Cell: ({ value }: { value: string }) => (
          <span className={styles.districtCell} title={value}>
            {value || "-"}
          </span>
        ),
        sort: false,
      },
      {
        Header: "Query Subject",
        accessor: "subject",
        Cell: ({ value }: { value: string }) => (
          <span className={styles.subjectCell} title={value}>
            {value}
          </span>
        ),
        sort: false,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }: { value: string }) => (
          <span className="text-nowrap">
            <StatusBadge status={value} />
          </span>
        ),
        sort: false,
      },
      {
        Header: "Action",
        accessor: "a",
        Cell: ({ row }: { row: any }) => (
          <div className="d-flex align-items-center gap-2">
            <QueryViewModal data={row.original} />
            <QueryUpdateModal
              data={row.original}
              onUpdated={handleQueryUpdated}
            />
          </div>
        ),
        sort: false,
      },
    ],
    [page, limit, handleQueryUpdated]
  );

  const handleFilterQuery = () => {
    if (!selectedLevel && !selectedStatusOfQuery && !selectedDistrict) {
      alert("Please select Level, Status and/or District");
      return;
    }
    setAppliedLevel(selectedLevel);
    setAppliedStatus(selectedStatusOfQuery);
    setAppliedDistrict(selectedDistrict);
    setPage(1);
  };

  const handleSelectDistrictFromSummary = (code: string) => {
    setSelectedDistrict(code);
    setAppliedDistrict(code);
    setPage(1);
  };

  const handleClearFilterQuery = () => {
    setSelectedLevel("");
    setSelectedStatusOfQuery("");
    setAppliedLevel("");
    setAppliedStatus("");
    setSelectedDistrict("");
    setAppliedDistrict("");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError("");
      }, 2500);
    }
  }, [error]);

  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(page * limit, total);

  // A short, centered window of page numbers instead of one item per page.
  const pageWindow = useMemo(() => {
    const windowSize = 5;
    let start = Math.max(1, page - Math.floor(windowSize / 2));
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    const items = [];
    for (let p = start; p <= end; p++) items.push(p);
    return items;
  }, [page, totalPages]);

  const isFiltered = Boolean(
    search || appliedLevel || appliedStatus || appliedDistrict
  );

  return (
    <>
      <PageTitle
        breadCrumbItems={[
          { label: "All Queries", path: "/query" },
          {
            label: "Query",
            path: "/query",
            active: true,
          },
        ]}
        title={"All Query"}
      />

      <Row>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="primary"
            title={
              appliedDistrict
                ? `Total Queries - ${appliedDistrictName}`
                : "Total Queries"
            }
            stats={overallTotal.toLocaleString()}
            icon="help-circle"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="danger"
            title="Open"
            stats={statusSummary.Open.toLocaleString()}
            icon="alert-circle"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="warning"
            title="Pending / In-Review"
            stats={(
              (statusSummary.Pending || 0) + (statusSummary["In-Review"] || 0)
            ).toLocaleString()}
            icon="clock"
          />
        </Col>
        <Col sm={6} xl={3}>
          <StatisticsWidget
            variant="success"
            title="Closed"
            stats={statusSummary.Closed.toLocaleString()}
            icon="check-circle"
          />
        </Col>
      </Row>

      <Row className="mb-3 g-2 g-md-3">
        <Col xs={12} md={6} lg={3}>
          <FormGroup>
            <FormLabel>Select Level</FormLabel>
            <FormControl
              as="select"
              value={selectedLevel}
              onChange={(e) => setSelectedLevel(e.target.value)}
            >
              <option value="">Select a Level</option>
              <option value="School">School</option>
              <option value="Block">Block</option>
              <option value="District">District</option>
              <option value="State">State</option>
            </FormControl>
          </FormGroup>
        </Col>
        <Col xs={12} md={6} lg={3}>
          <FormGroup>
            <FormLabel>Select Status of Query</FormLabel>
            <FormControl
              as="select"
              value={selectedStatusOfQuery}
              onChange={(e) => setSelectedStatusOfQuery(e.target.value)}
            >
              <option value="">Select a Status</option>
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
              <option value="Pending">Pending</option>
              <option value="In-Review">In-Review</option>
            </FormControl>
          </FormGroup>
        </Col>
        <Col xs={12} md={6} lg={3}>
          <FormGroup>
            <FormLabel>Select District</FormLabel>
            <FormControl
              as="select"
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
            >
              <option value="">All Districts</option>
              {districtSummary.map((d) => (
                <option key={d.code} value={d.code}>
                  {d.district} ({d.Open} open)
                </option>
              ))}
            </FormControl>
          </FormGroup>
        </Col>
        <Col xs={12} md={6} lg={3}>
          {error ? (
            <h3 style={{ backgroundColor: "silver", color: "black" }}>
              {error}
            </h3>
          ) : null}
          <div className={styles.filterActions}>
            <Button onClick={handleFilterQuery} disabled={loading}>
              Filter
            </Button>
            <Button
              variant="secondary"
              onClick={handleClearFilterQuery}
              disabled={loading}
            >
              Clear Filter
            </Button>
            <DistrictQuerySummaryModal
              onSelectDistrict={handleSelectDistrictFromSummary}
              refreshKey={summaryRefreshKey}
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <Form.Control
                  type="search"
                  placeholder="Search by name, email, phone or subject..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className={styles.searchInput}
                />
                <Form.Select
                  value={limit}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="w-auto"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size} / page
                    </option>
                  ))}
                </Form.Select>
              </div>

              {loading ? (
                <div style={{ textAlign: "center" }}>
                  <Spinner />
                </div>
              ) : (
                <>
                  <div className={styles.tableScrollArea}>
                    <Table
                      columns={columns}
                      data={data}
                      isSortable={false}
                      pagination={false}
                      isSearchable={false}
                    />
                  </div>

                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                    <span className="text-muted small">
                      {total === 0
                        ? "No queries found"
                        : `Showing ${rangeStart}–${rangeEnd} of ${total}${
                            isFiltered ? " (filtered)" : ""
                          }`}
                    </span>
                    {totalPages > 1 && (
                      <Pagination className="mb-0 flex-wrap">
                        <Pagination.First
                          disabled={page === 1}
                          onClick={() => setPage(1)}
                        />
                        <Pagination.Prev
                          disabled={page === 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        />
                        {pageWindow.map((p) => (
                          <Pagination.Item
                            key={p}
                            active={p === page}
                            onClick={() => setPage(p)}
                          >
                            {p}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          disabled={page === totalPages}
                          onClick={() =>
                            setPage((p) => Math.min(totalPages, p + 1))
                          }
                        />
                        <Pagination.Last
                          disabled={page === totalPages}
                          onClick={() => setPage(totalPages)}
                        />
                      </Pagination>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AllQuery;
